import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useGetLeadSource, useUpdateLead } from '@/application/services/lead.service'
import { useGetEntityTranslationState } from '@/application/services/translation.service'
import { useAuthStore } from '@/application/stores/auth.store'
import { resolveLanguage } from '@/application/i18n/language'
import type { Lead, TranslationsWritePayload } from '@/domains/types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/ui/components/common/Dialog'
import { Button } from '@/ui/components/common/Button'
import { Label } from '@/ui/components/common/Label'
import { Input } from '@/ui/components/common/Input'
import { RichTextEditor } from '@/ui/components/common/RichTextEditor'
import { ImageField } from '@/ui/components/forms/ImageField'
import { AuthorSelector, type AuthorState } from '@/ui/components/forms/AuthorSelector'
import { TranslationTabs } from '@/ui/components/translations/TranslationTabs'

interface EditLeadDialogProps {
  lead: Lead | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditLeadDialog({ lead, open, onOpenChange }: EditLeadDialogProps) {
  const { t } = useTranslation()
  const { mutateAsync: updateLead, isPending } = useUpdateLead()
  const { user } = useAuthStore()
  const canChangeAuthor = user?.role === 'streamer' || user?.role === 'admin'

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  // A lead identifies its author by username only, so the badge is fed from the
  // username on both sides and the id stays unused here.
  const [author, setAuthor] = useState<AuthorState>({ id: null, name: '', username: null })
  const [translations, setTranslations] = useState<TranslationsWritePayload>({})
  const { data: entityState, refetch: refetchEntityState } = useGetEntityTranslationState(
    'lead',
    lead?.id ?? '',
  )
  // The `lead` prop can come from a locale-resolved list/detail read (e.g.
  // `useGetLeadsByInvestigation`, which passes the admin's own UI locale as
  // `?lang=`) — its `title`/`description` may already be translated text,
  // not the true source. Editing must always start from the raw source row,
  // otherwise saving a translation would write the resolved (translated)
  // text back into the source columns. `useGetLeadSource` never sends
  // `?lang=`, so it always returns the untouched source fields.
  const { data: sourceLead, refetch: refetchSourceLead } = useGetLeadSource(lead?.id ?? '')

  // Sync form fields when the lead changes
  useEffect(() => {
    if (lead) {
      setTitle(sourceLead?.title ?? lead.title ?? '')
      setDescription(sourceLead?.description ?? lead.description ?? '')
      setImageUrl(lead.imageUrl ?? '')
      setAuthor({
        id: null,
        name: lead.submitterUsername ?? '',
        username: lead.submitterUsername ?? null,
      })
      setTranslations({})
    }
  }, [lead, sourceLead])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!lead) return
    try {
      const nextSubmitterUsername =
        canChangeAuthor && author.username && author.username !== lead.submitterUsername
          ? author.username
          : null
      await updateLead({
        id: lead.id,
        title,
        description,
        imageUrl: imageUrl || null,
        translations,
        ...(nextSubmitterUsername ? { submitterUsername: nextSubmitterUsername } : {}),
      })
      // Refetch this dialog's OWN data source before closing — its
      // `useGetLeadSource`/`useGetEntityTranslationState` queries may have
      // been fetching in the background since the dialog opened (before
      // this edit), and TanStack Query doesn't otherwise know to treat
      // that in-flight request as outdated. Calling `refetch()` here
      // starts a new, superseding fetch — its result is what the query
      // ends up holding, regardless of whether the earlier one resolves
      // first or last. Without this, reopening the dialog right after
      // saving could show the pre-save text if that earlier fetch happened
      // to land after this save completed.
      await Promise.all([refetchSourceLead(), refetchEntityState()])
      toast.success(t('admin.leads_edit_success'))
      onOpenChange(false)
    } catch (error: unknown) {
      toast.error(
        t('admin.leads_edit_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="wide" onPointerDownOutside={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('admin.leads_edit_dialog_title')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {lead && (
            <TranslationTabs
              sourceLocale={resolveLanguage(lead.sourceLocale ?? 'fr')}
              fields={[
                {
                  name: 'title',
                  labelKey: 'admin.leads_edit_title_label',
                  kind: 'input',
                  sourceValue: title,
                },
                {
                  name: 'description',
                  labelKey: 'admin.leads_edit_description_label',
                  kind: 'richtext',
                  sourceValue: description,
                  richTextEditorProps: { scrollable: true },
                },
              ]}
              entityState={entityState}
              onChange={setTranslations}
              disabled={isPending}
              renderSourceFields={() => (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-lead-title">{t('admin.leads_edit_title_label')}</Label>
                    <Input
                      id="edit-lead-title"
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      required
                      maxLength={200}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="edit-lead-description">
                      {t('admin.leads_edit_description_label')}
                    </Label>
                    <RichTextEditor
                      value={description}
                      onChange={setDescription}
                      disabled={isPending}
                      scrollable
                      onUploadError={(message) => toast.error(message)}
                    />
                  </div>
                </>
              )}
            />
          )}

          <div className="space-y-1.5">
            <Label>{t('admin.leads_edit_image_label')}</Label>
            <ImageField value={imageUrl} onChange={setImageUrl} disabled={isPending} />
          </div>

          {canChangeAuthor && (
            <div className="space-y-1.5">
              <Label>{t('admin.form_author_label')}</Label>
              <p className="text-sm text-muted-foreground">{t('admin.form_author_desc_lead')}</p>
              <AuthorSelector value={author} onChange={setAuthor} allowClear={false} />
            </div>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                {t('common.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isPending}>
              {t('admin.leads_edit_submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
